import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, TextField, Button, Paper, Grid, Tab, Tabs, 
  CircularProgress, Chip, Divider, Alert, Tooltip, Dialog, DialogTitle, 
  DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel, 
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody, 
  ListSubheader, IconButton, Collapse, Card, CardContent, Accordion,
  AccordionSummary, AccordionDetails, Switch, FormControlLabel
} from '@mui/material';
import { 
  Save, Refresh, Delete, ContentCopy, FlashOn, Add, Edit, ExpandMore, 
  Visibility, Code, Science, CompareArrows, Assessment, PlayArrow,
  Chat, Send, Lightbulb, Clear, Psychology
} from '@mui/icons-material';
import { AdminService } from '../../services/adminService';
import PromptContextForm from '../../components/admin/PromptContextForm';
import VariableHelper from '../../components/admin/VariableHelper';
import { 
  GAME_TYPES, ROUTES, getLessons, getThemes, DIFFICULTIES, getGameConfig,
  getAllCompetences, getCompetencesforLevel, getGameCompetences
} from '../../config/shared_config';
import JSONEditor from '../../components/admin/JSONEditor';
import LoadingScreen from '../../components/LoadingScreen';
import AdminLayout from '../../layouts/AdminLayout';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { chatWithLLMWebSocket } from '../../utils/backend_query';
import { toBlanks } from '../../utils/textHolesBlanks';

const VISUAL_PREVIEW_GAME_TYPES = new Set([
  'speed_game',
  'text_holes',
  'correction_game',
  'word_lists',
  'dictation',
  'association',
  'categorisation',
  'speed_sort',
]);

/** JSON formaté in-page (Ace, lecture seule) — évite le copier-coller bloc-notes. */
const RawJsonPreview = ({ content, title = 'Résultat JSON' }) => (
  <Box>
    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Code fontSize="small" />
      {title}
    </Typography>
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <JSONEditor value={content} readOnly height="360px" onChange={() => {}} />
    </Paper>
  </Box>
);

// Composant pour l'aperçu visuel de l'exercice (réutilisé depuis ExerciseDetailScreen)
const ExercisePreview = ({ gameType, content }) => {
  if (!content) return <Typography>Pas de contenu à afficher</Typography>;

  switch (gameType) {
    case 'speed_game':
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Aperçu des mots</Typography>
          <Grid container spacing={2}>
            {content.words && content.words.slice(0, 5).map((word, index) => (
              <Grid item xs={12} key={index}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {word.correct}
                  </Typography>
                  <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                    {word.options && word.options.map((option, idx) => (
                      <Chip 
                        key={idx} 
                        label={option} 
                        color={option === word.correct ? 'success' : 'default'}
                      />
                    ))}
                  </Box>
                  {word.usagePhrase && (
                    <Typography variant="body2" mt={1} fontStyle="italic">
                      {word.usagePhrase}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
            {content.words && content.words.length > 5 && (
              <Grid item xs={12}>
                <Typography color="textSecondary">
                  +{content.words.length - 5} autres mots...
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      );
    
    case 'text_holes': {
      const blanks = toBlanks(content);
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Texte à trous</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {content.text}
          </Typography>
          {content.completedText && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Complété : {content.completedText}
            </Typography>
          )}
          {blanks.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Trous ({blanks.length})</Typography>
              {blanks.map((blank, index) => (
                <Box key={index} display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1}>
                  <Chip label={blank.answer} color="success" size="small" />
                  {(blank.distractors || []).map((d, j) => (
                    <Chip key={j} label={d} variant="outlined" size="small" />
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }
    
    case 'correction_game':
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Jeu de correction</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {content.content}
          </Typography>
          {content.errors && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Erreurs à corriger :</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {content.errors.map((error, index) => (
                  <Chip key={index} label={error} color="error" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      );
    
    case 'word_lists':
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Liste de mots</Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {content.words && content.words.map((word, index) => (
              <Chip key={index} label={word} />
            ))}
          </Box>
        </Box>
      );
    
    case 'dictation':
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Dictée</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {content.text}
          </Typography>
        </Box>
      );

    case 'association':
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Association par paires</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>{content.enonce}</Typography>
          <Grid container spacing={1}>
            {(content.pairs || []).map((pair, index) => (
              <Grid item xs={12} key={index}>
                <Chip label={`${pair.left} → ${pair.right}`} variant="outlined" />
              </Grid>
            ))}
          </Grid>
        </Box>
      );

    case 'categorisation':
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Catégorisation</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>{content.enonce}</Typography>
          {(content.categories || []).map((category) => (
            <Box key={category.id} mb={1}>
              <Typography variant="subtitle2">{category.label}</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {(content.items || [])
                  .filter((item) => item.correctCategoryId === category.id)
                  .map((item, index) => (
                    <Chip key={index} label={item.text} variant="outlined" />
                  ))}
              </Box>
            </Box>
          ))}
        </Box>
      );

    case 'speed_sort':
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Speed Sort</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>{content.enonce}</Typography>
          {content.gestureStimulus && (
            <Chip
              size="small"
              label={`Stimulus : ${content.gestureStimulus}`}
              sx={{ mb: 1 }}
              color={content.gestureStimulus === 'spoken' ? 'info' : 'default'}
            />
          )}
          <Grid container spacing={2}>
            {['left', 'right'].map((side) => (
              <Grid item xs={12} md={6} key={side}>
                <Typography variant="subtitle2">{side === 'left' ? content.leftLabel : content.rightLabel}</Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {(content.sortItems || [])
                    .filter((item) => item.correctSide === side)
                    .map((item, index) => (
                      <Chip key={index} label={item.text} variant="outlined" />
                    ))}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    
    default:
      return <RawJsonPreview content={content} title="Résultat JSON (aperçu visuel non disponible pour ce type)" />;
  }
};

/** Aperçu visuel si disponible + JSON brut repliable en dessous. */
const TestResultPreview = ({ gameType, content }) => {
  if (!content) return <Typography color="text.secondary">Pas de contenu à afficher</Typography>;

  const hasVisualPreview = VISUAL_PREVIEW_GAME_TYPES.has(gameType);

  if (!hasVisualPreview) {
    return <RawJsonPreview content={content} title="Résultat JSON (aperçu visuel non disponible)" />;
  }

  return (
    <Box>
      <ExercisePreview gameType={gameType} content={content} />
      <Accordion
        disableGutters
        elevation={0}
        sx={{ mt: 2, '&:before': { display: 'none' }, border: 1, borderColor: 'divider', borderRadius: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Code fontSize="inherit" /> JSON brut
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <JSONEditor value={content} readOnly height="280px" onChange={() => {}} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const PromptEngineeringScreen = () => {
  // Hook WebSocket
  const socket = useWebSocket();
  
  // États pour la gestion des prompts
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPromptExamples, setShowPromptExamples] = useState(false);
  const [newPrompt, setNewPrompt] = useState({
    name: '',
    description: '',
    promptContent: '',
    contextFilters: {}
  });
  const [formatInstructions, setFormatInstructions] = useState('');
  const [loadingFormatInstructions, setLoadingFormatInstructions] = useState(false);
  
  const [parametersForm, setParametersForm] = useState({
    schoolLevel: 'CE2',
    difficulty: 'medium',
    theme: '',
    competence: null,
    gameType: 'speed_game',
    totalWords: 10,
    numOptions: 4,
    mode: 'text',
    useCustomPrompt: false,
    selectedPromptId: '',
    customPrompt: ''
  });

  // Récupérer les compétences et thèmes selon le niveau
  const { themes = {}, lessons = {} } = getGameCompetences(parametersForm.schoolLevel) || {};
  const allCompetences = getAllCompetences() || [];


  const [promptPreview, setPromptPreview] = useState({
    content: '',
    variables: {},
    metadata: null,
    loading: false,
    error: null
  });
  const [testResults, setTestResults] = useState({
    content: null,
    loading: false,
    error: null,
    metadata: null
  });
  const [promptComparison, setPromptComparison] = useState({
    variants: [],
    results: [],
    loading: false
  });
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [currentPromptVersion, setCurrentPromptVersion] = useState('');

  const [promptHistory, setPromptHistory] = useState([]);

  // États pour le chat avec LLM
  const [chatMessages, setChatMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatContext, setChatContext] = useState('');
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // États pour le streaming WebSocket
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [cleanupChatSocket, setCleanupChatSocket] = useState(null);
  
  // États pour le système d'évaluation et d'optimisation
  const [optimizationData, setOptimizationData] = useState({
    originalExercise: null,
    firstEvaluation: null,
    optimizedPrompt: null,
    finalExercise: null,
    isOptimizing: false,
    optimizationStep: 0 // 0: initial, 1: première évaluation, 2: optimisation prompt, 3: régénération finale
  });
  const [showOptimizationDialog, setShowOptimizationDialog] = useState(false);
  
  // Calculer le total des tokens utilisés
  const totalTokensUsed = chatMessages.reduce((total, message) => {
    return total + (message.metadata?.tokensUsed || 0);
  }, 0);

  // Fonction pour lancer l'évaluation et l'optimisation
  const handleOptimizePrompt = async () => {
    if (!testResults.content) {
      setErrorMessage('Veuillez d\'abord générer un exercice pour l\'évaluer');
      return;
    }

    setOptimizationData(prev => ({
      ...prev,
      isOptimizing: true,
      optimizationStep: 1,
      originalExercise: testResults.content
    }));

    // Naviguer vers l'onglet Optimisation pour voir la progression
    setActiveTab(4);

    try {
      // Étape 1: Générer l'exercice initial
      const generationParams = {
        gameType: parametersForm.gameType,
        parameters: {
          schoolLevel: parametersForm.schoolLevel,
          difficulty: parametersForm.difficulty,
          theme: parametersForm.theme,
          competence: parametersForm.competence,
          totalWords: parametersForm.totalWords,
          numOptions: parametersForm.numOptions,
          mode: parametersForm.mode
        }
      };

      if (parametersForm.useCustomPrompt) {
        if (parametersForm.selectedPromptId) {
          generationParams.customPromptId = parametersForm.selectedPromptId;
        } else if (parametersForm.customPrompt) {
          generationParams.customPrompt = parametersForm.customPrompt;
        }
      }

      // Étape 2: Première évaluation
      const firstEvaluation = await AdminService.evaluateExercise({
        exercise: testResults.content,
        parameters: generationParams.parameters,
        gameType: generationParams.gameType
      });

      setOptimizationData(prev => ({
        ...prev,
        firstEvaluation,
        optimizationStep: 2
      }));

      // Étape 3: Optimisation du prompt
      console.log('🚀 Début de l\'étape 3: Optimisation du prompt');
      
      // Vérifier que tous les paramètres requis sont présents
      const originalPrompt = parametersForm.customPrompt || 'Prompt par défaut pour génération d\'exercices';
      console.log('Paramètres envoyés:', {
        originalPrompt: originalPrompt.substring(0, 100),
        gameType: generationParams.gameType,
        parameters: generationParams.parameters,
        firstEvaluation: firstEvaluation
      });
      
      // Vérifier que firstEvaluation est bien structuré
      if (!firstEvaluation || typeof firstEvaluation.competenceScore !== 'number') {
        throw new Error('Évaluation manquante ou invalide');
      }
      
      const optimizedPrompt = await AdminService.optimizePrompt({
        originalPrompt: originalPrompt,
        originalExercise: testResults.content,
        firstEvaluation: firstEvaluation,
        adaptedExercise: null,
        secondEvaluation: null,
        parameters: generationParams.parameters,
        gameType: generationParams.gameType
      });
      
      // Vérifier que optimizedPrompt est bien une chaîne
      const promptText = typeof optimizedPrompt === 'string' ? optimizedPrompt : optimizedPrompt?.optimizedPrompt || optimizedPrompt;
      
      if (!promptText || typeof promptText !== 'string') {
        throw new Error('Prompt optimisé invalide reçu du serveur');
      }
      
      console.log('✅ Prompt optimisé reçu:', promptText.substring(0, 100) + '...');

      setOptimizationData(prev => ({
        ...prev,
        optimizedPrompt: promptText,
        optimizationStep: 3
      }));

      // Étape 4: Régénération avec le prompt optimisé
      console.log('🔄 Début de l\'étape 4: Régénération avec le prompt optimisé');
      const finalGenerationParams = {
        ...generationParams,
        customPrompt: promptText
      };
      
      console.log('Paramètres de génération finale:', finalGenerationParams);

      const finalExercise = await AdminService.generateExercise(finalGenerationParams);
      console.log('✅ Exercice final généré:', finalExercise);

      setOptimizationData(prev => ({
        ...prev,
        finalExercise,
        optimizationStep: 4,
        isOptimizing: false
      }));

      // Naviguer vers l'onglet Optimisation
      setActiveTab(4);
      setShowOptimizationDialog(true);

    } catch (error) {
      console.error('Erreur lors de l\'optimisation:', error);
      setErrorMessage('Erreur lors de l\'optimisation du prompt');
      setOptimizationData(prev => ({
        ...prev,
        isOptimizing: false,
        optimizationStep: 0
      }));
    }
  };

  const [activeTab, setActiveTab] = useState(0);

  // États pour la génération en masse
  const [showBulkGenerateDialog, setShowBulkGenerateDialog] = useState(false);
  const [bulkGenerateParams, setBulkGenerateParams] = useState({
    gameType: 'speed_game',
    schoolLevel: 'CE2',
    exercisesPerCombination: 2,
    // Nouvelle sélection des compétences
    competenceSelection: {
      mode: 'none', // 'none', 'all', 'domain', 'specific'
      selectedDomain: '',
      selectedCompetences: [], // Array d'objets compétences sélectionnées
      availableDomains: [],
      availableCompetences: []
    },
    includeThemes: true,
    // Nouveau : gestion du prompt personnalisé
    useCustomPrompt: false,
    customPrompt: '',
    selectedPromptId: '',
    // Paramètres spécifiques aux jeux
    totalWords: 10,
    numOptions: 4,
    mode: 'text'
  });
  const [bulkGenerateProgress, setBulkGenerateProgress] = useState({
    isGenerating: false,
    current: 0,
    total: 0,
    currentItem: '',
    results: [],
    shouldStop: false
  });
  
  // Nouveau : état pour la prévisualisation du prompt en génération masse
  const [bulkPromptPreview, setBulkPromptPreview] = useState({
    content: '',
    loading: false,
    error: null
  });

  // Référence pour pouvoir arrêter la génération en cours
  const shouldStopRef = React.useRef(false);

  // Paramètres spécifiques par type de jeu
  const gameSpecificParams = {
    'speed_game': ['totalWords', 'numOptions'],
    'text_holes': ['totalWords'],
    'correction_game': [],
    'word_lists': ['totalWords'],
    'dictation': ['totalWords', 'mode'],
    'association': ['totalWords'],
    'categorisation': ['totalWords'],
    'speed_sort': ['totalWords']
  };

  // Fonction pour vérifier si un paramètre est applicable au type de jeu actuel
  const isParamApplicable = (param) => {
    return gameSpecificParams[parametersForm.gameType]?.includes(param);
  };

  // Cette fonction transforme l'identifiant d'une compétence en structure complète
  const formatCompetenceForBackend = (competenceId) => {
    if (!competenceId) return null;
    
    const selectedCompetence = allCompetences.find(comp => 
      `${comp.domain}-${comp.competence}-${comp.notion}` === competenceId
    );
    
    if (!selectedCompetence) return null;
    
    return {
      domain: selectedCompetence.domain,
      competence: selectedCompetence.competence,
      notion: selectedCompetence.notion
    };
  };

  // Exemples de prompts par type de jeu
  const getPromptExamples = (gameType) => {
    const examples = {
      'speed_game': [
        {
          name: "Conjugaison au futur",
          prompt: `Génère {{totalWords}} mots de conjugaison au futur pour le niveau {{schoolLevel}}.
Concentre-toi sur les verbes du 1er groupe et les verbes être/avoir.
Chaque mot doit avoir {{numOptions}} options avec des erreurs typiques de conjugaison.
Difficulté: {{difficulty}}`
        },
        {
          name: "Homonymes courants",
          prompt: `Crée {{totalWords}} exercices sur les homonymes pour {{schoolLevel}}.
Utilise des paires comme: vert/verre, mer/mère, son/sont, etc.
Inclus des phrases d'usage pour contextualiser chaque mot.
Niveau de difficulté: {{difficulty}}`
        }
      ],
      'text_holes': [
        {
          name: "Texte narratif avec accords",
          prompt: `Écris un petit texte narratif de 4-5 phrases pour {{schoolLevel}}.
Thème: {{theme}}
Crée {{totalWords}} trous focalisés sur les accords (sujet-verbe, adjectifs).
Le texte doit être engageant et adapté au niveau {{difficulty}}.`
        }
      ],
      'correction_game': [
        {
          name: "Erreurs d'orthographe courantes",
          prompt: `Crée un texte de 3-4 phrases avec des erreurs d'orthographe typiques du niveau {{schoolLevel}}.
Inclus des erreurs sur: les doubles consonnes, les accents, les terminaisons.
Thème: {{theme}}
Difficulté: {{difficulty}}`
        }
      ],
      'word_lists': [
        {
          name: "Vocabulaire thématique",
          prompt: `Crée une liste de {{totalWords}} mots sur le thème {{theme}}.
Niveau {{schoolLevel}}, difficulté {{difficulty}}.
Inclus des mots variés: noms, verbes, adjectifs liés au thème.`
        }
      ],
      'dictation': [
        {
          name: "Dictée narrative",
          prompt: `Écris une dictée de {{totalWords}} mots environ pour {{schoolLevel}}.
Crée une petite histoire sur le thème {{theme}}.
Inclus des difficultés orthographiques adaptées au niveau.
Difficulté: {{difficulty}}`
        }
      ]
    };
    
    return examples[gameType] || [];
  };

  useEffect(() => {
    loadAvailablePrompts(parametersForm.gameType);
    loadFormatInstructions(parametersForm.gameType);
  }, [parametersForm.gameType]);

  const handleParametersChange = (field, value) => {
    setParametersForm(prev => ({ ...prev, [field]: value }));
  };

  const loadAvailablePrompts = async (gameType) => {
    try {
      const resp = await AdminService.getPrompts({ gameType });
      const items = resp?.prompts || resp?.data?.prompts || [];
      setAvailablePrompts(items);
    } catch (error) {
      console.error('Erreur lors du chargement des prompts:', error);
      setAvailablePrompts([]);
    }
  };

  const loadFormatInstructions = async (gameType) => {
    setLoadingFormatInstructions(true);
    try {
      const instructions = await AdminService.getFormatInstructions(gameType);
      setFormatInstructions(instructions || '');
    } catch (error) {
      console.error('Erreur lors du chargement des instructions:', error);
      setFormatInstructions('');
    } finally {
      setLoadingFormatInstructions(false);
    }
  };

  const previewPrompt = async () => {
    setPromptPreview(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await AdminService.previewPrompt({
        gameType: parametersForm.gameType,
        parameters: {
          schoolLevel: parametersForm.schoolLevel,
          difficulty: parametersForm.difficulty,
          theme: parametersForm.theme,
          competence: formatCompetenceForBackend(parametersForm.competence),
          totalWords: parametersForm.totalWords,
          numOptions: parametersForm.numOptions,
          mode: parametersForm.mode
        },
        customPrompt: parametersForm.customPrompt,
        selectedPromptId: parametersForm.selectedPromptId
      });
      
      setPromptPreview({
        content: response.prompt,
        variables: response.variables,
        metadata: response.metadata || null,
        loading: false,
        error: null
      });
      
      // Retourner le contenu pour l'utiliser directement
      return response.prompt;
    } catch (error) {
      console.error('Error in previewPrompt:', error);
      setPromptPreview(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
      throw error;
    }
  };

  const testPrompt = async (customPromptContent = null) => {
    setTestResults(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await AdminService.testPrompt({
        gameType: parametersForm.gameType,
        parameters: {
          schoolLevel: parametersForm.schoolLevel,
          difficulty: parametersForm.difficulty,
          theme: parametersForm.theme,
          competence: formatCompetenceForBackend(parametersForm.competence),
          totalWords: parametersForm.totalWords,
          numOptions: parametersForm.numOptions,
          mode: parametersForm.mode
        },
        customPrompt: customPromptContent || parametersForm.customPrompt,
        selectedPromptId: parametersForm.selectedPromptId
      });
      
      setTestResults({
        content: response.generatedContent,
        loading: false,
        error: null,
        metadata: response.metadata
      });
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  const savePromptVersion = async (promptContent, name, description) => {
    try {
      const response = await AdminService.savePromptVersion({
        gameType: parametersForm.gameType,
        name,
        description,
        promptContent,
        contextFilters: newPrompt.contextFilters,
        parameters: {
          schoolLevel: parametersForm.schoolLevel,
          difficulty: parametersForm.difficulty,
          theme: parametersForm.theme,
          competence: formatCompetenceForBackend(parametersForm.competence),
          totalWords: parametersForm.totalWords,
          numOptions: parametersForm.numOptions,
          mode: parametersForm.mode
        }
      });
      
      // Recharger la liste des prompts
      await loadAvailablePrompts(parametersForm.gameType);
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const handleSavePrompt = async () => {
    try {
      await savePromptVersion(
        newPrompt.promptContent,
        newPrompt.name,
        newPrompt.description
      );
      
      setShowPromptDialog(false);
      setNewPrompt({ name: '', description: '', promptContent: '', contextFilters: {} });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  // ✨ FONCTIONS POUR LA GÉNÉRATION EN MASSE

  // Calculer le nombre total de combinaisons
  const calculateTotalCombinations = () => {
    const { themes = {}, lessons = {} } = getGameCompetences(bulkGenerateParams.schoolLevel) || {};
    let total = 0;
    
    // Utiliser la nouvelle logique de sélection des compétences
    const filteredCompetences = getFilteredCompetences();
    total += filteredCompetences.length;
    
    if (bulkGenerateParams.includeThemes) {
      total += Object.values(themes).length;
    }
    
    return total * bulkGenerateParams.exercisesPerCombination;
  };

  // Générer en masse
  const handleBulkGenerate = async () => {
    shouldStopRef.current = false; // Réinitialiser le flag d'arrêt
    setBulkGenerateProgress(prev => ({ 
      ...prev, 
      isGenerating: true, 
      current: 0, 
      results: [], 
      shouldStop: false 
    }));
    
    try {
      const { themes = {}, lessons = {} } = getGameCompetences(bulkGenerateParams.schoolLevel) || {};
      const combinations = [];
      
      // Ajouter les compétences sélectionnées
      const filteredCompetences = getFilteredCompetences();
      filteredCompetences.forEach(comp => {
        for (let i = 0; i < bulkGenerateParams.exercisesPerCombination; i++) {
          combinations.push({
            type: 'competence',
            data: comp,
            title: `${comp.domain} - ${comp.competence} - ${comp.notion} (${i + 1})`
          });
        }
      });
      
      // Ajouter les thèmes
      if (bulkGenerateParams.includeThemes) {
        Object.values(themes).forEach(theme => {
          for (let i = 0; i < bulkGenerateParams.exercisesPerCombination; i++) {
            combinations.push({
              type: 'theme',
              data: theme,
              title: `Thème: ${theme.notion} (${i + 1})`
            });
          }
        });
      }
      
      setBulkGenerateProgress(prev => ({ ...prev, total: combinations.length }));
      
      const results = [];
      
      // Générer chaque exercice
      for (let i = 0; i < combinations.length; i++) {
        // Vérifier si l'utilisateur a demandé l'arrêt
        if (shouldStopRef.current) {
          console.log('Génération arrêtée par l\'utilisateur');
          setBulkGenerateProgress(prev => ({
            ...prev,
            results: [...prev.results, {
              success: false,
              title: 'Génération interrompue',
              error: 'Arrêtée par l\'utilisateur',
              type: 'system'
            }]
          }));
          break;
        }
        
        const combination = combinations[i];
        
        setBulkGenerateProgress(prev => ({
          ...prev,
          current: i + 1,
          currentItem: combination.title
        }));
        
        try {
          // Vérifier à nouveau avant de commencer la génération
          if (shouldStopRef.current) {
            break;
          }
          
          // Préparer les paramètres de génération
          const generationParams = {
            gameType: bulkGenerateParams.gameType,
            parameters: {
              schoolLevel: bulkGenerateParams.schoolLevel,
              difficulty: 'medium'
            },
            // Ajouter le prompt personnalisé si sélectionné
            customPrompt: bulkGenerateParams.useCustomPrompt ? bulkGenerateParams.customPrompt : '',
            selectedPromptId: bulkGenerateParams.selectedPromptId
          };
          
          if (combination.type === 'competence') {
            generationParams.parameters.competence = {
              domain: combination.data.domain,
              competence: combination.data.competence,
              notion: combination.data.notion
            };
          } else {
            generationParams.parameters.theme = combination.data.notion;
          }
          
          // Ajouter les paramètres spécifiques au jeu
          if (isParamApplicable('totalWords')) {
            generationParams.parameters.totalWords = bulkGenerateParams.totalWords;
          }
          if (isParamApplicable('numOptions') && bulkGenerateParams.gameType === 'speed_game') {
            generationParams.parameters.numOptions = bulkGenerateParams.numOptions;
          }
          if (bulkGenerateParams.gameType === 'dictation') {
            generationParams.parameters.mode = bulkGenerateParams.mode;
          }
          
          // Générer l'exercice
          const generationResult = await AdminService.generateExercise(generationParams);
          
          // Vérifier encore une fois avant de sauvegarder
          if (bulkGenerateProgress.shouldStop) {
            break;
          }
          
          // Créer l'exercice en base
          const exerciseData = {
            type: 'llm',
            gameType: bulkGenerateParams.gameType,
            parameters: generationParams.parameters,
            content: generationResult.generatedContent,
            title: combination.title,
            description: `Exercice généré automatiquement - ${combination.title}`,
            isActive: true,
            source: 'LLM-bulk' // Source spécifique pour la génération en masse
          };
          
          const createdExercise = await AdminService.createExercise(exerciseData);
          
          results.push({
            success: true,
            title: combination.title,
            exerciseId: createdExercise.id,
            type: combination.type
          });
          
          // Mettre à jour les résultats en temps réel
          setBulkGenerateProgress(prev => ({
            ...prev,
            results: [...prev.results, {
              success: true,
              title: combination.title,
              exerciseId: createdExercise.id,
              type: combination.type
            }]
          }));
          
          // Petite pause pour éviter de surcharger le serveur et permettre l'arrêt
          await new Promise(resolve => setTimeout(resolve, 500));
          
        } catch (error) {
          console.error(`Erreur pour ${combination.title}:`, error);
          const errorResult = {
            success: false,
            title: combination.title,
            error: error.message,
            type: combination.type
          };
          
          results.push(errorResult);
          
          // Mettre à jour les résultats en temps réel
          setBulkGenerateProgress(prev => ({
            ...prev,
            results: [...prev.results, errorResult]
          }));
        }
      }
      
      setBulkGenerateProgress(prev => ({
        ...prev,
        isGenerating: false,
        shouldStop: false
      }));
      
    } catch (error) {
      console.error('Erreur lors de la génération en masse:', error);
      setBulkGenerateProgress(prev => ({
        ...prev,
        isGenerating: false,
        shouldStop: false
      }));
    }
  };

  // Fonction pour arrêter la génération
  const handleStopBulkGenerate = () => {
    shouldStopRef.current = true; // Marquer pour arrêt
    setBulkGenerateProgress(prev => ({
      ...prev,
      shouldStop: true,
      currentItem: 'Arrêt en cours...'
    }));
  };

  // Dialogue pour la génération en masse
  const renderBulkGenerateDialog = () => {
    const totalCombinations = calculateTotalCombinations();
    const { themes = {}, lessons = {} } = getGameCompetences(bulkGenerateParams.schoolLevel) || {};
    
    return (
      <Dialog 
        open={showBulkGenerateDialog} 
        onClose={() => !bulkGenerateProgress.isGenerating && setShowBulkGenerateDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          🚀 Génération en Masse d'Exercices
        </DialogTitle>
        <DialogContent>
          {!bulkGenerateProgress.isGenerating ? (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Configuration de base */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Configuration de base
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type d'exercice</InputLabel>
                  <Select
                    value={bulkGenerateParams.gameType}
                    onChange={(e) => {
                      const newGameType = e.target.value;
                      setBulkGenerateParams(prev => {
                        const updated = { ...prev, gameType: newGameType };
                        
                        // Définir des valeurs par défaut selon le type de jeu
                        switch (newGameType) {
                          case 'speed_game':
                            updated.totalWords = 10;
                            updated.numOptions = 4;
                            break;
                          case 'text_holes':
                            updated.totalWords = 6; // Nombre de trous
                            break;
                          case 'word_lists':
                            updated.totalWords = 10; // Nombre de mots
                            break;
                          case 'dictation':
                            updated.totalWords = 20; // Longueur approximative
                            updated.mode = 'text';
                            break;
                          case 'association':
                          case 'categorisation':
                          case 'speed_sort':
                            updated.totalWords = 6; // Nombre d'items
                            break;
                          default:
                            updated.totalWords = 10;
                            break;
                        }
                        
                        return updated;
                      });
                    }}
                    label="Type d'exercice"
                  >
                    {Object.values(GAME_TYPES || {}).map((game) => (
                      <MenuItem key={game.id} value={game.id}>{game.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Niveau scolaire</InputLabel>
                  <Select
                    value={bulkGenerateParams.schoolLevel}
                    onChange={(e) => setBulkGenerateParams(prev => ({ ...prev, schoolLevel: e.target.value }))}
                    label="Niveau scolaire"
                  >
                    {['CP', 'CE1', 'CE2', 'CM1', 'CM2', '6ème'].map((level) => (
                      <MenuItem key={level} value={level}>{level}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Nombre d'exercices par combinaison"
                  value={bulkGenerateParams.exercisesPerCombination}
                  onChange={(e) => setBulkGenerateParams(prev => ({ 
                    ...prev, 
                    exercisesPerCombination: Math.max(1, parseInt(e.target.value) || 1)
                  }))}
                  InputProps={{ inputProps: { min: 1, max: 10 } }}
                  helperText="Nombre d'exercices à générer pour chaque compétence/thème"
                />
              </Grid>
              
              {/* Paramètres spécifiques aux jeux */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                  Paramètres des exercices
                </Typography>
              </Grid>
              
              {/* Paramètres selon le type de jeu */}
              {isParamApplicable('totalWords') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={bulkGenerateParams.gameType === 'dictation' ? "Nombre de mots approximatif" : 
                           bulkGenerateParams.gameType === 'text_holes' ? "Nombre de trous" : 
                           "Nombre de mots"}
                    value={bulkGenerateParams.totalWords}
                    onChange={(e) => setBulkGenerateParams(prev => ({ 
                      ...prev, 
                      totalWords: Math.max(5, parseInt(e.target.value) || 10)
                    }))}
                    InputProps={{ inputProps: { min: 5, max: 100 } }}
                    helperText="Nombre de mots/trous pour chaque exercice généré"
                  />
                </Grid>
              )}
              
              {isParamApplicable('numOptions') && bulkGenerateParams.gameType === 'speed_game' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Nombre d'options par mot"
                    value={bulkGenerateParams.numOptions}
                    onChange={(e) => setBulkGenerateParams(prev => ({ 
                      ...prev, 
                      numOptions: Math.max(2, parseInt(e.target.value) || 4)
                    }))}
                    InputProps={{ inputProps: { min: 2, max: 6 } }}
                    helperText="Nombre de choix multiples pour chaque mot"
                  />
                </Grid>
              )}
              
              {bulkGenerateParams.gameType === 'dictation' && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Mode de dictée</InputLabel>
                    <Select
                      value={bulkGenerateParams.mode}
                      onChange={(e) => setBulkGenerateParams(prev => ({ ...prev, mode: e.target.value }))}
                      label="Mode de dictée"
                    >
                      <MenuItem value="text">Texte continu</MenuItem>
                      <MenuItem value="word_lists">Liste de mots</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              {/* Sélection du contenu - Compétences */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                  Sélection des Compétences
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Mode de sélection des compétences</InputLabel>
                  <Select
                    value={bulkGenerateParams.competenceSelection.mode}
                    onChange={(e) => {
                      const mode = e.target.value;
                      setBulkGenerateParams(prev => ({
                        ...prev,
                        competenceSelection: {
                          ...prev.competenceSelection,
                          mode: mode,
                          selectedDomain: '',
                          selectedCompetences: []
                        }
                      }));
                    }}
                    label="Mode de sélection des compétences"
                  >
                    <MenuItem value="none">Aucune compétence</MenuItem>
                    <MenuItem value="all">Toutes les compétences ({(allCompetences || []).length})</MenuItem>
                    <MenuItem value="domain">Par domaine</MenuItem>
                    <MenuItem value="specific">Sélection spécifique</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Sélection par domaine */}
              {bulkGenerateParams.competenceSelection.mode === 'domain' && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Domaine</InputLabel>
                    <Select
                      value={bulkGenerateParams.competenceSelection.selectedDomain}
                      onChange={(e) => {
                        const domain = e.target.value;
                        setBulkGenerateParams(prev => ({
                          ...prev,
                          competenceSelection: {
                            ...prev.competenceSelection,
                            selectedDomain: domain
                          }
                        }));
                      }}
                      label="Domaine"
                    >
                      {getAvailableDomains().map((domain) => (
                        <MenuItem key={domain} value={domain}>
                          {domain} ({getCompetencesByDomain(domain).length} compétences)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Sélection spécifique */}
              {bulkGenerateParams.competenceSelection.mode === 'specific' && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Compétences spécifiques</InputLabel>
                    <Select
                      multiple
                      value={bulkGenerateParams.competenceSelection.selectedCompetences.map(c => 
                        `${c.domain}-${c.competence}-${c.notion}`
                      )}
                      onChange={(e) => {
                        const selectedIds = e.target.value;
                        const selectedComps = selectedIds.map(id => {
                          return (allCompetences || []).find(comp => 
                            `${comp.domain}-${comp.competence}-${comp.notion}` === id
                          );
                        }).filter(Boolean);
                        
                        setBulkGenerateParams(prev => ({
                          ...prev,
                          competenceSelection: {
                            ...prev.competenceSelection,
                            selectedCompetences: selectedComps
                          }
                        }));
                      }}
                      label="Compétences spécifiques"
                      renderValue={(selected) => `${selected.length} compétences sélectionnées`}
                    >
                      {getAvailableDomains().map((domain) => [
                        <ListSubheader key={domain}>{domain}</ListSubheader>,
                        ...getCompetencesByDomain(domain).map((comp) => (
                          <MenuItem 
                            key={`${comp.domain}-${comp.competence}-${comp.notion}`} 
                            value={`${comp.domain}-${comp.competence}-${comp.notion}`}
                          >
                            {comp.competence} - {comp.notion}
                          </MenuItem>
                        ))
                      ])}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Affichage du nombre de compétences sélectionnées */}
              {bulkGenerateParams.competenceSelection.mode !== 'none' && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Compétences sélectionnées :</strong> {getFilteredCompetences().length} compétences
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {/* Sélection des thèmes */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                  Sélection des Thèmes
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={bulkGenerateParams.includeThemes}
                      onChange={(e) => setBulkGenerateParams(prev => ({ 
                        ...prev, 
                        includeThemes: e.target.checked 
                      }))}
                    />
                  }
                  label={`Inclure les thèmes (${Object.values(themes).length} disponibles)`}
                />
              </Grid>

              {/* Configuration du prompt */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                  Configuration du Prompt
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={bulkGenerateParams.useCustomPrompt}
                      onChange={(e) => setBulkGenerateParams(prev => ({ 
                        ...prev, 
                        useCustomPrompt: e.target.checked 
                      }))}
                    />
                  }
                  label="Utiliser un prompt personnalisé"
                />
              </Grid>

              {bulkGenerateParams.useCustomPrompt && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Prompt pré-enregistré</InputLabel>
                      <Select
                        value={bulkGenerateParams.selectedPromptId}
                        onChange={(e) => setBulkGenerateParams(prev => ({ 
                          ...prev, 
                          selectedPromptId: e.target.value 
                        }))}
                        label="Prompt pré-enregistré"
                      >
                        <MenuItem value="">Aucun (utiliser le prompt personnalisé)</MenuItem>
                        {availablePrompts.map((prompt) => (
                          <MenuItem key={prompt.id} value={prompt.id}>
                            {prompt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={previewBulkPrompt}
                        disabled={bulkPromptPreview.loading}
                        startIcon={bulkPromptPreview.loading ? <CircularProgress size={16} /> : <Visibility />}
                      >
                        {bulkPromptPreview.loading ? 'Génération...' : 'Prévisualiser'}
                      </Button>
                    </Box>
                  </Grid>

                  {!bulkGenerateParams.selectedPromptId && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Prompt personnalisé"
                        value={bulkGenerateParams.customPrompt}
                        onChange={(e) => setBulkGenerateParams(prev => ({ 
                          ...prev, 
                          customPrompt: e.target.value 
                        }))}
                        placeholder="Entrez votre prompt personnalisé ici..."
                        helperText="Vous pouvez utiliser des variables comme {{schoolLevel}}, {{theme}}, {{competence}}, etc."
                      />
                    </Grid>
                  )}

                  {/* Prévisualisation du prompt */}
                  {bulkPromptPreview.content && (
                    <Grid item xs={12}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1">
                            📝 Aperçu du prompt qui sera utilisé
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 300, overflow: 'auto' }}>
                            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                              {bulkPromptPreview.content}
                            </Typography>
                          </Paper>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  )}

                  {bulkPromptPreview.error && (
                    <Grid item xs={12}>
                      <Alert severity="error">
                        {bulkPromptPreview.error}
                      </Alert>
                    </Grid>
                  )}
                </>
              )}
              
              {/* Résumé */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'primary.50' }}>
                  <Typography variant="h6" gutterBottom>
                    📊 Résumé de la génération
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2">
                        <strong>Type:</strong> {getGameConfig(bulkGenerateParams.gameType)?.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2">
                        <strong>Niveau:</strong> {bulkGenerateParams.schoolLevel}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2">
                        <strong>Difficulté:</strong> Medium (fixe)
                      </Typography>
                    </Grid>
                    {/* Paramètres spécifiques aux jeux */}
                    {isParamApplicable('totalWords') && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">
                          <strong>{bulkGenerateParams.gameType === 'dictation' ? "Mots approximatif" : 
                                   bulkGenerateParams.gameType === 'text_holes' ? "Nombre de trous" : 
                                   "Nombre de mots"}:</strong> {bulkGenerateParams.totalWords}
                        </Typography>
                      </Grid>
                    )}
                    {isParamApplicable('numOptions') && bulkGenerateParams.gameType === 'speed_game' && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">
                          <strong>Options par mot:</strong> {bulkGenerateParams.numOptions}
                        </Typography>
                      </Grid>
                    )}
                    {bulkGenerateParams.gameType === 'dictation' && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">
                          <strong>Mode:</strong> {bulkGenerateParams.mode === 'text' ? 'Texte continu' : 'Liste de mots'}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="body1" color="primary" sx={{ mt: 1 }}>
                        <strong>Total d'exercices à générer: {totalCombinations}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getFilteredCompetences().length > 0 && `${getFilteredCompetences().length} compétences × ${bulkGenerateParams.exercisesPerCombination} = ${getFilteredCompetences().length * bulkGenerateParams.exercisesPerCombination}`}
                        {getFilteredCompetences().length > 0 && bulkGenerateParams.includeThemes && ' + '}
                        {bulkGenerateParams.includeThemes && `${Object.values(themes).length} thèmes × ${bulkGenerateParams.exercisesPerCombination} = ${Object.values(themes).length * bulkGenerateParams.exercisesPerCombination}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <strong>Sélection de compétences:</strong> {bulkGenerateParams.competenceSelection.mode === 'none' ? 'Aucune' : 
                          bulkGenerateParams.competenceSelection.mode === 'all' ? 'Toutes' :
                          bulkGenerateParams.competenceSelection.mode === 'domain' ? `Domaine: ${bulkGenerateParams.competenceSelection.selectedDomain}` :
                          `${getFilteredCompetences().length} compétences spécifiques`}
                      </Typography>
                      {bulkGenerateParams.useCustomPrompt && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Prompt:</strong> {bulkGenerateParams.selectedPromptId ? 'Prompt pré-enregistré' : 'Prompt personnalisé'}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Avertissement */}
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    ⚠️ <strong>Attention:</strong> Cette opération va générer {totalCombinations} exercices. 
                    Cela peut prendre plusieurs minutes et consommer des crédits LLM. 
                    Assurez-vous que c'est bien ce que vous souhaitez faire.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          ) : (
            // Interface de progression
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Génération en cours...
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {bulkGenerateProgress.current} / {bulkGenerateProgress.total} exercices générés
                </Typography>
                <Box sx={{ width: '100%', mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <Box
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.300',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            bgcolor: 'primary.main',
                            borderRadius: 4,
                            width: `${(bulkGenerateProgress.current / bulkGenerateProgress.total) * 100}%`,
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 35 }}>
                      {Math.round((bulkGenerateProgress.current / bulkGenerateProgress.total) * 100)}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {bulkGenerateProgress.currentItem && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>En cours:</strong> {bulkGenerateProgress.currentItem}
                </Typography>
              )}
              
              {/* Résultats en temps réel */}
              {bulkGenerateProgress.results.length > 0 && (
                <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Résultats:
                  </Typography>
                  {bulkGenerateProgress.results.slice(-5).map((result, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Chip
                        size="small"
                        label={result.success ? '✅' : '❌'}
                        color={result.success ? 'success' : 'error'}
                        sx={{ mr: 1, minWidth: 40 }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {result.title}
                      </Typography>
                    </Box>
                  ))}
                  {bulkGenerateProgress.results.length > 5 && (
                    <Typography variant="body2" color="text.secondary">
                      ... et {bulkGenerateProgress.results.length - 5} autres
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!bulkGenerateProgress.isGenerating ? (
            <>
              <Button onClick={() => setShowBulkGenerateDialog(false)}>
                Annuler
              </Button>
              <Button
                variant="contained"
                onClick={handleBulkGenerate}
                disabled={totalCombinations === 0}
                color="warning"
              >
                Générer {totalCombinations} exercices
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleStopBulkGenerate}
                color="error"
                variant="contained"
                disabled={bulkGenerateProgress.shouldStop}
              >
                {bulkGenerateProgress.shouldStop ? 'Arrêt en cours...' : 'Arrêter la génération'}
              </Button>
              <Button
                onClick={() => setShowBulkGenerateDialog(false)}
                disabled={bulkGenerateProgress.isGenerating && !bulkGenerateProgress.shouldStop}
              >
                Fermer
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderPromptDialog = () => (
    <Dialog open={showPromptDialog} onClose={() => setShowPromptDialog(false)} maxWidth="md" fullWidth>
      <DialogTitle>Sauvegarder un nouveau prompt</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nom du prompt"
              value={newPrompt.name}
              onChange={(e) => setNewPrompt(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ex: Conjugaison CE2 - Futur simple"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description (optionnelle)"
              value={newPrompt.description}
              onChange={(e) => setNewPrompt(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description du prompt et de son usage..."
            />
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info">
              Le contenu du prompt actuel sera sauvegardé avec ces informations.
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              🎯 Ciblage Automatique du Prompt
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Comment ça fonctionne ?</strong> Le système sélectionnera automatiquement ce prompt 
                quand les paramètres de génération correspondent aux filtres ci-dessous.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Scoring :</strong> Plus il y a de correspondances, plus le score est élevé.
                Le prompt avec le meilleur score est utilisé. En cas d'égalité, la priorité départage.
              </Typography>
            </Alert>
            <PromptContextForm 
              value={newPrompt.contextFilters}
              onChange={(val) => setNewPrompt(prev => ({ ...prev, contextFilters: val }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowPromptDialog(false)}>Annuler</Button>
        <Button 
          variant="contained" 
          onClick={handleSavePrompt}
          disabled={!newPrompt.name.trim()}
        >
          Sauvegarder
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ✨ NOUVELLES FONCTIONS POUR LA GÉNÉRATION EN MASSE

  // Fonction pour obtenir les domaines disponibles
  const getAvailableDomains = () => {
    const domains = [...new Set((allCompetences || []).map(comp => comp.domain))];
    return domains.sort();
  };

  // Fonction pour obtenir les compétences filtrées selon la sélection
  const getFilteredCompetences = () => {
    const { competenceSelection } = bulkGenerateParams;
    
    switch (competenceSelection.mode) {
      case 'none':
        return [];
      case 'all':
        return allCompetences || [];
      case 'domain':
        return (allCompetences || []).filter(comp => 
          comp.domain === competenceSelection.selectedDomain
        );
      case 'specific':
        return competenceSelection.selectedCompetences || [];
      default:
        return [];
    }
  };

  // Fonction pour obtenir les compétences d'un domaine spécifique
  const getCompetencesByDomain = (domain) => {
    return (allCompetences || []).filter(comp => comp.domain === domain);
  };

  // Fonction pour prévisualiser le prompt qui sera utilisé en génération masse
  const previewBulkPrompt = async () => {
    setBulkPromptPreview(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Utiliser un exemple de compétence ou thème pour la prévisualisation
      const filteredCompetences = getFilteredCompetences();
      const { themes = {} } = getGameCompetences(bulkGenerateParams.schoolLevel) || {};
      
      let sampleParams = {
        schoolLevel: bulkGenerateParams.schoolLevel,
        difficulty: 'medium',
        totalWords: bulkGenerateParams.totalWords,
        numOptions: bulkGenerateParams.numOptions,
        mode: bulkGenerateParams.mode
      };

      // Ajouter un exemple de compétence ou thème
      if (filteredCompetences.length > 0) {
        const sampleComp = filteredCompetences[0];
        sampleParams.competence = {
          domain: sampleComp.domain,
          competence: sampleComp.competence,
          notion: sampleComp.notion
        };
      } else if (bulkGenerateParams.includeThemes && Object.values(themes).length > 0) {
        sampleParams.theme = Object.values(themes)[0].notion;
      }

      const response = await AdminService.previewPrompt({
        gameType: bulkGenerateParams.gameType,
        parameters: sampleParams,
        customPrompt: bulkGenerateParams.useCustomPrompt ? bulkGenerateParams.customPrompt : '',
        selectedPromptId: bulkGenerateParams.selectedPromptId
      });
      
      setBulkPromptPreview({
        content: response.prompt,
        loading: false,
        error: null
      });
    } catch (error) {
      setBulkPromptPreview(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  // ✨ FONCTIONS POUR LE CHAT AVEC LLM

  // Fonction utilitaire pour formater l'affichage des tokens
  const formatTokensDisplay = (metadata) => {
    if (!metadata?.tokensUsed) return '';
    
    const totalTokens = metadata.tokensUsed;
    let tokenInfo = `🪙 ${totalTokens}`;
    
    // Si on a les détails (prompt + completion tokens)
    if (metadata.promptTokens && metadata.completionTokens) {
      tokenInfo += ` (${metadata.promptTokens}+${metadata.completionTokens})`;
    } else {
      // Pour les messages utilisateur, on ajoute un indicateur d'estimation
      tokenInfo += ' (est.)';
    }
    
    // Ajouter le temps de réponse si disponible
    if (metadata.responseTime) {
      const timeInSeconds = (metadata.responseTime / 1000).toFixed(1);
      tokenInfo += ` • ${timeInSeconds}s`;
    }
    
    return tokenInfo;
  };

  // Envoyer un message au LLM avec streaming WebSocket
  const sendMessage = async () => {
    if (!currentMessage.trim()) return;
    
    // Nettoyer toute connexion WebSocket précédente
    if (cleanupChatSocket) {
      cleanupChatSocket();
      setCleanupChatSocket(null);
    }
    
    const userMessage = {
      role: 'user',
      content: currentMessage.trim(),
      timestamp: new Date().toISOString(),
      metadata: {
        tokensUsed: Math.ceil(currentMessage.trim().length / 4) // Estimation simple: ~4 chars par token
      }
    };
    
    // Ajouter le message utilisateur
    setChatMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setChatLoading(true);
    
    // Créer un message de réponse vide pour le streaming
    const assistantMessageId = Date.now().toString();
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    };
    
    // Ajouter le message assistant vide
    setChatMessages(prev => [...prev, assistantMessage]);
    setStreamingMessageId(assistantMessageId);
    
    try {
      // Construire le contexte en incluant les paramètres actuels
      let contextInfo = chatContext;
      if (!contextInfo.trim()) {
        contextInfo = `Contexte actuel: Type de jeu=${parametersForm.gameType}, Niveau=${parametersForm.schoolLevel}`;
        if (parametersForm.theme) contextInfo += `, Thème=${parametersForm.theme}`;
        if (parametersForm.competence) contextInfo += `, Compétence=${parametersForm.competence}`;
      }
      
      // Vérifier que WebSocket est disponible
      if (!socket) {
        throw new Error('WebSocket non disponible, impossible d\'utiliser le streaming');
      }
      
      // Démarrer le streaming WebSocket
      const cleanup = chatWithLLMWebSocket(
        [...chatMessages, userMessage],
        contextInfo,
        {
         
          maxTokens: 1000
        },
        socket,
        // Callback pour recevoir les chunks
        (chunk, accumulatedContent) => {
          console.log('📝 Chunk reçu:', chunk);
          setChatMessages(prev => prev.map(msg => 
            msg.id === assistantMessageId 
              ? { ...msg, content: accumulatedContent, isStreaming: true }
              : msg
          ));
        },
        // Callback de fin
        (finalContent, metadata) => {
          console.log('✅ Streaming terminé, contenu final:', finalContent);
          setChatMessages(prev => prev.map(msg => 
            msg.id === assistantMessageId 
              ? { 
                  ...msg, 
                  content: finalContent, 
                  isStreaming: false,
                  metadata: metadata
                }
              : msg
          ));
          setStreamingMessageId(null);
          setChatLoading(false);
        },
        // Callback d'erreur
        (error) => {
          console.error('❌ Erreur lors du streaming:', error);
          setChatMessages(prev => prev.map(msg => 
            msg.id === assistantMessageId 
              ? { 
                  ...msg, 
                  content: `Désolé, une erreur s'est produite : ${error}`,
                  isStreaming: false,
                  isError: true
                }
              : msg
          ));
          setStreamingMessageId(null);
          setChatLoading(false);
        }
      );
      
      // Stocker la fonction de nettoyage
      setCleanupChatSocket(() => cleanup);
      
    } catch (error) {
      console.error('Erreur lors du démarrage du chat:', error);
      // Supprimer le message assistant vide en cas d'erreur
      setChatMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
      
      // Ajouter un message d'erreur
      const errorMessage = {
        role: 'assistant',
        content: `Désolé, une erreur s'est produite : ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setChatMessages(prev => [...prev, errorMessage]);
      
      setStreamingMessageId(null);
      setChatLoading(false);
    }
  };

  // Vider la conversation
  const clearChat = () => {
    // Nettoyer toute connexion WebSocket active
    if (cleanupChatSocket) {
      cleanupChatSocket();
      setCleanupChatSocket(null);
    }
    setStreamingMessageId(null);
    setChatMessages([]);
    setSuggestedPrompts([]);
    setChatLoading(false);
  };

  // Nettoyage au démontage du composant
  useEffect(() => {
    return () => {
      if (cleanupChatSocket) {
        cleanupChatSocket();
      }
    };
  }, [cleanupChatSocket]);

  // Obtenir des suggestions de prompts via LLM
  const getSuggestedPromptsForChat = async () => {
    setLoadingSuggestions(true);
    
    try {
      const response = await AdminService.getSuggestedPrompts({
        gameType: parametersForm.gameType,
        parameters: {
          schoolLevel: parametersForm.schoolLevel,
          difficulty: parametersForm.difficulty,
          theme: parametersForm.theme,
          competence: formatCompetenceForBackend(parametersForm.competence)
        },
        description: chatContext || `Exercices de ${parametersForm.gameType} pour ${parametersForm.schoolLevel}`
      });
      
      setSuggestedPrompts(response.suggestions || []);
      
      // Ajouter un message dans le chat pour informer l'utilisateur
      const systemMessage = {
        role: 'assistant',
        content: `J'ai généré ${response.suggestions?.length || 0} suggestions de prompts pour vos exercices de ${parametersForm.gameType}. Vous pouvez les consulter dans le panneau de droite.`,
        timestamp: new Date().toISOString(),
        isSystem: true
      };
      
      setChatMessages(prev => [...prev, systemMessage]);
    } catch (error) {
      console.error('Erreur lors de la génération de suggestions:', error);
      const errorMessage = {
        role: 'assistant',
        content: `Erreur lors de la génération de suggestions : ${error.message}`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Copier un prompt dans l'éditeur
  const copyPromptToEditor = (promptContent) => {
    setCurrentPromptVersion(promptContent);
    setActiveTab(0); // Revenir au premier onglet
    
    // Ajouter un message de confirmation dans le chat
    const systemMessage = {
      role: 'assistant',
      content: 'Le prompt a été copié dans l\'éditeur de l\'onglet "Preview et test de prompts". Vous pouvez maintenant le modifier et le tester.',
      timestamp: new Date().toISOString(),
      isSystem: true
    };
    
    setChatMessages(prev => [...prev, systemMessage]);
  };

  // Fonction pour rendre le dialogue d'optimisation
  const renderOptimizationDialog = () => {
    const { originalExercise, firstEvaluation, adaptedExercise, secondEvaluation, optimizedPrompt, isOptimizing, optimizationStep } = optimizationData;

    return (
      <Dialog 
        open={showOptimizationDialog} 
        onClose={() => setShowOptimizationDialog(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Science />
            Évaluation et Optimisation du Prompt
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Indicateur de progression */}
            {isOptimizing && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Progression de l'optimisation :
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {[1, 2, 3, 4, 5].map((step) => (
                    <Box
                      key={step}
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: optimizationStep >= step ? 'primary.main' : 'grey.300',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: 'white'
                      }}
                    >
                      {step}
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {optimizationStep === 1 && 'Évaluation initiale...'}
                  {optimizationStep === 2 && 'Adaptation de l\'exercice...'}
                  {optimizationStep === 3 && 'Seconde évaluation...'}
                  {optimizationStep === 4 && 'Optimisation du prompt...'}
                </Typography>
              </Box>
            )}

            {/* Première évaluation */}
            {firstEvaluation && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📊 Première Évaluation
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: firstEvaluation.competenceScore >= 8 ? 'success.light' : 'warning.light' }}>
                        <Typography variant="h4" color={firstEvaluation.competenceScore >= 8 ? 'success.main' : 'warning.main'}>
                          {firstEvaluation.competenceScore}/10
                        </Typography>
                        <Typography variant="body2">
                          Évaluation de la compétence
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: !firstEvaluation.hasErrors ? 'success.light' : 'error.light' }}>
                        <Typography variant="h4" color={!firstEvaluation.hasErrors ? 'success.main' : 'error.main'}>
                          {firstEvaluation.hasErrors ? '❌' : '✅'}
                        </Typography>
                        <Typography variant="body2">
                          Erreurs détectées
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light' }}>
                        <Typography variant="h4" color="info.main">
                          {firstEvaluation.consistencyScore || 'N/A'}/10
                        </Typography>
                        <Typography variant="body2">
                          Cohérence générale
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  {firstEvaluation.feedback && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Commentaires détaillés :
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        {firstEvaluation.feedback}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Exercice adapté */}
            {adaptedExercise && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🔄 Exercice Adapté
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <TestResultPreview
                      gameType={parametersForm.gameType}
                      content={adaptedExercise}
                    />
                  </Paper>
                </CardContent>
              </Card>
            )}

            {/* Seconde évaluation */}
            {secondEvaluation && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📊 Seconde Évaluation
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: secondEvaluation.competenceScore >= 8 ? 'success.light' : 'warning.light' }}>
                        <Typography variant="h4" color={secondEvaluation.competenceScore >= 8 ? 'success.main' : 'warning.main'}>
                          {secondEvaluation.competenceScore}/10
                        </Typography>
                        <Typography variant="body2">
                          Évaluation de la compétence
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: !secondEvaluation.hasErrors ? 'success.light' : 'error.light' }}>
                        <Typography variant="h4" color={!secondEvaluation.hasErrors ? 'success.main' : 'error.main'}>
                          {secondEvaluation.hasErrors ? '❌' : '✅'}
                        </Typography>
                        <Typography variant="body2">
                          Erreurs détectées
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light' }}>
                        <Typography variant="h4" color="info.main">
                          {secondEvaluation.consistencyScore || 'N/A'}/10
                        </Typography>
                        <Typography variant="body2">
                          Cohérence générale
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  {secondEvaluation.feedback && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Commentaires détaillés :
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        {secondEvaluation.feedback}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Prompt optimisé */}
            {optimizedPrompt && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🚀 Prompt Optimisé
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    value={optimizedPrompt}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      startIcon={<ContentCopy />}
                      onClick={() => navigator.clipboard.writeText(optimizedPrompt)}
                    >
                      Copier le prompt optimisé
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setParametersForm(prev => ({
                          ...prev,
                          customPrompt: optimizedPrompt,
                          useCustomPrompt: true
                        }));
                        setShowOptimizationDialog(false);
                      }}
                    >
                      Utiliser ce prompt
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOptimizationDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <AdminLayout>
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
        `}
      </style>
      <Box sx={{ p: 0 }}>
        {/* En-tête */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">
            🧠 Prompt Engineering
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => {
              setNewPrompt(prev => ({ ...prev, promptContent: currentPromptVersion }));
              setShowPromptDialog(true);
            }}
            disabled={!currentPromptVersion.trim()}
          >
            Sauvegarder le prompt
          </Button>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="🔧 Preview et test de prompts" />
            <Tab label="🚀 Génération en masse" />
            <Tab label="💬 Chat avec LLM" />
            <Tab label="📊 Comparaison" />
            <Tab label="🧪 Optimisation" />
          </Tabs>
        </Paper>

        {/* Contenu des tabs */}
        {activeTab === 0 && (
                   <Paper sx={{ p: 3 }}>
                   <Typography variant="h5" gutterBottom color="primary">
                     🧠 Preview et Test de Prompts
                   </Typography>
                   
                   <Grid container spacing={3}>
                     {/* Section de configuration des paramètres */}
                     <Grid item xs={12}>
                       <Card>
                         <CardContent>
                           <Typography variant="h6" gutterBottom>
                             Configuration des paramètres
                           </Typography>
                           <Grid container spacing={2}>
                             <Grid item xs={12} md={4}>
                               <FormControl fullWidth>
                                 <InputLabel>Type de jeu</InputLabel>
                                 <Select
                                   value={parametersForm.gameType}
                                   onChange={(e) => handleParametersChange('gameType', e.target.value)}
                                   label="Type de jeu"
                                 >
                                   {Object.values(GAME_TYPES || {}).map((game) => (
                                     <MenuItem key={game.id} value={game.id}>{game.label}</MenuItem>
                                   ))}
                                 </Select>
                               </FormControl>
                             </Grid>
                             <Grid item xs={12} md={4}>
                               <FormControl fullWidth>
                                 <InputLabel>Niveau</InputLabel>
                                 <Select
                                   value={parametersForm.schoolLevel}
                                   onChange={(e) => handleParametersChange('schoolLevel', e.target.value)}
                                   label="Niveau"
                                 >
                                   {['CP', 'CE1', 'CE2', 'CM1', 'CM2', '6ème'].map((level) => (
                                     <MenuItem key={level} value={level}>{level}</MenuItem>
                                   ))}
                                 </Select>
                               </FormControl>
                             </Grid>
                             <Grid item xs={12} md={4}>
                               <FormControl fullWidth>
                                 <InputLabel>Difficulté</InputLabel>
                                 <Select
                                   value={parametersForm.difficulty}
                                   onChange={(e) => handleParametersChange('difficulty', e.target.value)}
                                   label="Difficulté"
                                 >
                                   {Object.values(DIFFICULTIES || {}).map((diff) => (
                                     <MenuItem key={diff.id} value={diff.id}>{diff.label}</MenuItem>
                                   ))}
                                 </Select>
                               </FormControl>
                             </Grid>
                             
                             {/* Sélection de compétence */}
                             <Grid item xs={12} md={6}>
                               <FormControl fullWidth>
                                 <InputLabel>Compétence</InputLabel>
                                 <Select
                                   value={parametersForm.competence || ''}
                                   onChange={(e) => {
                                     handleParametersChange('competence', e.target.value);
                                     // Si on sélectionne une compétence, effacer le thème
                                     if (e.target.value) handleParametersChange('theme', '');
                                   }}
                                   label="Compétence"
                                 >
                                   <MenuItem value="">Aucune</MenuItem>
                                   {(allCompetences || []).map(comp => (
                                     <MenuItem 
                                       key={`${comp.domain}-${comp.competence}-${comp.notion}`} 
                                       value={`${comp.domain}-${comp.competence}-${comp.notion}`}
                                     >
                                       {comp.domain}: {comp.competence} - {comp.notion}
                                     </MenuItem>
                                   ))}
                                 </Select>
                               </FormControl>
                             </Grid>
                             
                             {/* Sélection de thème (si aucune compétence n'est sélectionnée) */}
                             <Grid item xs={12} md={6}>
                               <FormControl fullWidth>
                                 <InputLabel>Thème</InputLabel>
                                 <Select
                                   value={parametersForm.theme || ''}
                                   onChange={(e) => {
                                     handleParametersChange('theme', e.target.value);
                                     // Si on sélectionne un thème, effacer la compétence
                                     if (e.target.value) handleParametersChange('competence', '');
                                   }}
                                   label="Thème"
                                   disabled={!!parametersForm.competence} // Désactiver si une compétence est sélectionnée
                                 >
                                   <MenuItem value="">Aucun</MenuItem>
                                   {Object.values(themes || {}).map((theme) => (
                                     <MenuItem key={theme.notion} value={theme.notion}>{theme.notion}</MenuItem>
                                   ))}
                                 </Select>
                               </FormControl>
                             </Grid>
                             
                             {/* Paramètres spécifiques aux jeux */}
                             {isParamApplicable('totalWords') && (
                               <Grid item xs={12} md={6}>
                                 <TextField
                                   fullWidth
                                   type="number"
                                   label={parametersForm.gameType === 'dictation' ? "Nombre de mots approximatif" : 
                                          parametersForm.gameType === 'text_holes' ? "Nombre de trous" : 
                                          "Nombre de mots"}
                                   value={parametersForm.totalWords}
                                   onChange={(e) => handleParametersChange('totalWords', e.target.value)}
                                   InputProps={{ inputProps: { min: 5, max: 100 } }}
                                 />
                               </Grid>
                             )}
                             
                             {isParamApplicable('numOptions') && parametersForm.gameType === 'speed_game' && (
                               <Grid item xs={12} md={6}>
                                 <TextField
                                   fullWidth
                                   type="number"
                                   label="Nombre d'options par mot"
                                   value={parametersForm.numOptions}
                                   onChange={(e) => handleParametersChange('numOptions', e.target.value)}
                                   InputProps={{ inputProps: { min: 2, max: 6 } }}
                                 />
                               </Grid>
                             )}
                           </Grid>
                           
                           {/* Alerte informative sur compétence vs thème */}
                           <Alert severity="info" sx={{ mt: 2 }}>
                             <Typography variant="body2">
                               💡 <strong>Conseil :</strong> Sélectionnez soit une <strong>compétence</strong> pour un exercice ciblé sur une notion précise, 
                               soit un <strong>thème</strong> pour un exercice de vocabulaire général. 
                               {!parametersForm.competence && !parametersForm.theme && (
                                 <span style={{ color: 'orange' }}> Pour de meilleurs résultats, choisissez l'un des deux.</span>
                               )}
                             </Typography>
                           </Alert>

                           {promptPreview.metadata && (
                             <Alert severity="success" sx={{ mt: 2 }}>
                               <Typography variant="body2">
                                 <strong>Source du prompt :</strong>{' '}
                                 {promptPreview.metadata.promptSource === 'custom_prompt_db' && 'prompt personnalisé (BDD, context_filters)'}
                                 {promptPreview.metadata.promptSource === 'custom_manual' && 'prompt personnalisé (saisie manuelle)'}
                                 {promptPreview.metadata.promptSource === 'default_framework' && 'prompt runtime (cadres gestureGames + compétence)'}
                                 {promptPreview.metadata.promptSource === 'default' && 'prompt par défaut du type de jeu'}
                                 {promptPreview.metadata.sortingFrameworkId && (
                                   <> — cadre : <code>{promptPreview.metadata.sortingFrameworkId}</code></>
                                 )}
                                 {promptPreview.metadata.gestureStimulus && (
                                   <> — affichage items : <code>{promptPreview.metadata.gestureStimulus}</code></>
                                 )}
                               </Typography>
                             </Alert>
                           )}
                           
                                              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              variant="contained"
                              startIcon={<Visibility />}
                              onClick={async () => {
                                try {
                                  if (currentPromptVersion && currentPromptVersion.trim() !== '') {
                                    if (window.confirm('⚠️ Voulez-vous écraser le prompt actuel avec le prompt basé sur vos paramètres ?')) {
                                      const promptContent = await previewPrompt();
                                      if (promptContent) {
                                        setCurrentPromptVersion(promptContent);
                                      }
                                    }
                                  } else {
                                    const promptContent = await previewPrompt();
                                    if (promptContent) {
                                      setCurrentPromptVersion(promptContent);
                                    }
                                  }
                                } catch (error) {
                                  console.error('❌ Erreur lors du chargement du prompt:', error);
                                }
                              }}
                              disabled={promptPreview.loading}
                              color="primary"
                            >
                              {promptPreview.loading ? 'Chargement...' : 'Charger le Prompt'}
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<Science />}
                              onClick={() => testPrompt()}
                              disabled={testResults.loading}
                              color="secondary"
                            >
                              {testResults.loading ? 'Test en cours...' : 'Tester le Prompt'}
                            </Button>
                          </Box>
                         </CardContent>
                       </Card>
                     </Grid>
       
                     {/* Section Édition (gauche) */}
                     <Grid item xs={12} md={8}>
                       <Card sx={{ height: '100%' }}>
                         <CardContent>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Edit sx={{ mr: 1 }} />
                            Édition du Prompt
                          </Typography>

                          {/* Sélecteur de prompts personnalisés */}
                          <Box sx={{ mb: 2 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Charger un prompt personnalisé</InputLabel>
                                  <Select
                                    value=""
                                    label="Charger un prompt personnalisé"
                                    onChange={(e) => {
                                      const selectedPrompt = availablePrompts.find(p => p.id === e.target.value);
                                      if (selectedPrompt) {
                                        if (currentPromptVersion && currentPromptVersion.trim() !== '') {
                                          if (window.confirm('⚠️ Voulez-vous écraser vos modifications actuelles ?')) {
                                            setCurrentPromptVersion(selectedPrompt.prompt_content);
                                          }
                                        } else {
                                          setCurrentPromptVersion(selectedPrompt.prompt_content);
                                        }
                                      }
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      <em>-- Sélectionnez un prompt --</em>
                                    </MenuItem>
                                    {availablePrompts.length === 0 ? (
                                      <MenuItem value="" disabled>
                                        <em>Aucun prompt sauvegardé pour ce type de jeu</em>
                                      </MenuItem>
                                    ) : (
                                      availablePrompts.map((prompt) => (
                                        <MenuItem key={prompt.id} value={prompt.id}>
                                          {prompt.name}
                                          {prompt.description && (
                                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                              - {prompt.description.substring(0, 50)}...
                                            </Typography>
                                          )}
                                        </MenuItem>
                                      ))
                                    )}
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Box>

                          {promptPreview.error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                              {promptPreview.error}
                            </Alert>
                          )}
                          
                          <TextField
                            fullWidth
                            multiline
                            rows={30}
                            label="Prompt"
                            value={currentPromptVersion}
                            onChange={(e) => setCurrentPromptVersion(e.target.value)}
                            placeholder="Chargez un prompt personnalisé ou le prompt par défaut pour commencer... Utilisez {{variable}} pour insérer des variables dynamiques"
                            helperText={`💡 Cliquez sur une variable à droite pour l'insérer. Syntaxe : {{nomVariable}} (${currentPromptVersion?.length || 0} caractères)`}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'aria-label': 'Édition du prompt' }}
                            sx={{ 
                              mb: 2,
                              '& .MuiInputBase-input': {
                                fontSize: '12px',
                                fontFamily: 'monospace'
                              }
                            }}
                          />
                           
                           <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                             <Button
                               size="medium"
                               variant="outlined"
                               onClick={() => setCurrentPromptVersion('')}
                               disabled={!currentPromptVersion}
                               startIcon={<Clear />}
                             >
                               Effacer
                             </Button>
                             <Button
                               size="medium"
                               variant="contained"
                               color="secondary"
                               onClick={() => testPrompt(currentPromptVersion)}
                               disabled={!currentPromptVersion || testResults.loading}
                               startIcon={testResults.loading ? <CircularProgress size={16} /> : <Science />}
                             >
                               {testResults.loading ? 'Test en cours...' : 'Tester le prompt'}
                             </Button>
                           </Box>
                         </CardContent>
                       </Card>
                     </Grid>

                     {/* Panneau des variables (droite) */}
                     <Grid item xs={12} md={4}>
                       <Card sx={{ height: '100%', position: 'sticky', top: 20 }}>
                         <VariableHelper 
                           gameType={parametersForm.gameType} 
                           onInsertVariable={(varText) => {
                             const textarea = document.querySelector('textarea[aria-label="Édition du prompt"]');
                             if (textarea) {
                               const start = textarea.selectionStart;
                               const end = textarea.selectionEnd;
                               const text = currentPromptVersion;
                               const newText = text.substring(0, start) + varText + text.substring(end);
                               setCurrentPromptVersion(newText);
                               setTimeout(() => {
                                 textarea.focus();
                                 textarea.setSelectionRange(start + varText.length, start + varText.length);
                               }, 0);
                             }
                           }}
                         />
                       </Card>
                     </Grid>
       
                     {/* Section résultats du test (en bas sur toute la largeur) */}
                     <Grid item xs={12}>
                       <Card>
                         <CardContent>
                           <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                             <Assessment sx={{ mr: 1 }} />
                             Résultats du Test
                           </Typography>
                           
                           {testResults.error && (
                             <Alert severity="error" sx={{ mb: 2 }}>
                               {testResults.error}
                             </Alert>
                           )}
                           
                           {testResults.loading ? (
                             <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                               <CircularProgress />
                             </Box>
                           ) : testResults.content ? (
                             <>
                               {/* Métadonnées du test */}
                               {testResults.metadata && (
                                 <Box sx={{ mb: 2 }}>
                                   <Grid container spacing={1}>
                                     {testResults.metadata.generationTime && (
                                       <Grid item>
                                         <Chip
                                           label={`⏱️ ${testResults.metadata.generationTime}ms`}
                                           size="small"
                                           color="primary"
                                         />
                                       </Grid>
                                     )}
                                     {testResults.metadata.tokensUsed && (
                                       <Grid item>
                                         <Chip
                                           label={`🪙 ${testResults.metadata.tokensUsed} tokens`}
                                           size="small"
                                           color="secondary"
                                         />
                                       </Grid>
                                     )}
                                     {testResults.metadata.qualityScore && (
                                       <Grid item>
                                         <Chip
                                           label={`⭐ Score: ${testResults.metadata.qualityScore}/10`}
                                           size="small"
                                           color="success"
                                         />
                                       </Grid>
                                     )}
                                   </Grid>
                                 </Box>
                               )}
                               
                               {/* Contenu généré */}
                               <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                                 <TestResultPreview
                                   gameType={parametersForm.gameType}
                                   content={testResults.content}
                                 />
                               </Paper>
                               
                               <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                 <Button
                                   size="small"
                                   startIcon={<ContentCopy />}
                                   onClick={() => navigator.clipboard.writeText(JSON.stringify(testResults.content, null, 2))}
                                 >
                                   Copier le résultat JSON
                                 </Button>
                                 <Button
                                   size="small"
                                   variant="outlined"
                                   onClick={() => testPrompt(currentPromptVersion || promptPreview.content)}
                                   disabled={testResults.loading}
                                 >
                                   Retester
                                 </Button>
                                 <Button
                                   size="small"
                                   variant="contained"
                                   color="primary"
                                   startIcon={<Science />}
                                   onClick={handleOptimizePrompt}
                                   disabled={optimizationData.isOptimizing}
                                 >
                                   {optimizationData.isOptimizing ? 'Optimisation...' : 'Évaluer et optimiser'}
                                 </Button>
                               </Box>
                             </>
                           ) : (
                             <Typography color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
                               Cliquez sur "Tester le Prompt" pour voir les résultats générés
                             </Typography>
                           )}
                         </CardContent>
                       </Card>
                     </Grid>
                   </Grid>
                 </Paper>
       
         )}

        {/* Tab Génération en Masse */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">
                🚀 Génération en Masse d'Exercices
              </Typography>
              <Button
                variant="contained"
                color="warning"
                startIcon={<FlashOn />}
                onClick={() => setShowBulkGenerateDialog(true)}
                disabled={bulkGenerateProgress.isGenerating}
              >
                Lancer la génération en masse
              </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                La génération en masse permet de créer automatiquement des exercices pour toutes les compétences 
                et thèmes disponibles d'un niveau donné. Cette fonctionnalité utilise les prompts par défaut 
                ou vos prompts personnalisés pour générer du contenu de qualité.
              </Typography>
            </Alert>

            {/* Résumé de la configuration actuelle */}
            <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Configuration actuelle pour la génération en masse:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Type d'exercice:</strong> {getGameConfig(bulkGenerateParams.gameType)?.label || 'Non défini'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Niveau:</strong> {bulkGenerateParams.schoolLevel}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Exercices par combinaison:</strong> {bulkGenerateParams.exercisesPerCombination}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Total estimé:</strong> {calculateTotalCombinations()} exercices
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Statistiques en temps réel si génération en cours */}
            {bulkGenerateProgress.isGenerating && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
                <Typography variant="h6" gutterBottom>
                  Génération en cours...
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {bulkGenerateProgress.current} / {bulkGenerateProgress.total} exercices générés
                  </Typography>
                  <Box sx={{ width: '100%', mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Box
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'grey.300',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              bgcolor: 'primary.main',
                              borderRadius: 4,
                              width: `${(bulkGenerateProgress.current / bulkGenerateProgress.total) * 100}%`,
                              transition: 'width 0.3s ease'
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 35 }}>
                        {Math.round((bulkGenerateProgress.current / bulkGenerateProgress.total) * 100)}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                {bulkGenerateProgress.currentItem && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>En cours:</strong> {bulkGenerateProgress.currentItem}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={handleStopBulkGenerate}
                    color="error"
                    variant="outlined"
                    disabled={bulkGenerateProgress.shouldStop}
                  >
                    {bulkGenerateProgress.shouldStop ? 'Arrêt en cours...' : 'Arrêter la génération'}
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Résultats de la dernière génération */}
            {bulkGenerateProgress.results.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Résultats de la génération
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Succès:</strong> {bulkGenerateProgress.results.filter(r => r.success).length} / {bulkGenerateProgress.results.length}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Échecs:</strong> {bulkGenerateProgress.results.filter(r => !r.success).length} / {bulkGenerateProgress.results.length}
                  </Typography>
                </Box>

                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {bulkGenerateProgress.results.map((result, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1, p: 1, bgcolor: result.success ? 'success.50' : 'error.50', borderRadius: 1 }}>
                      <Chip
                        size="small"
                        label={result.success ? '✅' : '❌'}
                        color={result.success ? 'success' : 'error'}
                        sx={{ mr: 2, minWidth: 40 }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {result.title}
                        </Typography>
                        {result.error && (
                          <Typography variant="caption" color="error">
                            Erreur: {result.error}
                          </Typography>
                        )}
                        {result.exerciseId && (
                          <Typography variant="caption" color="text.secondary">
                            ID: {result.exerciseId}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        size="small"
                        label={result.type}
                        variant="outlined"
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Paper>
        )}

        {/* Tab Chat avec LLM */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom color="primary">
              💬 Chat avec le LLM
            </Typography>
            
            <Grid container spacing={3}>
              {/* Section contexte */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <Psychology sx={{ mr: 1 }} />
                      Contexte de discussion
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Contexte (optionnel)"
                      value={chatContext}
                      onChange={(e) => setChatContext(e.target.value)}
                      placeholder="Décrivez le contexte de votre discussion (ex: Je travaille sur des exercices de conjugaison pour CE2...)"
                      helperText="Ce contexte sera fourni au LLM pour améliorer ses réponses"
                    />
                    
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        startIcon={<Lightbulb />}
                        onClick={getSuggestedPromptsForChat}
                        disabled={loadingSuggestions}
                        size="small"
                      >
                        {loadingSuggestions ? 'Génération...' : 'Obtenir des suggestions de prompts'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Clear />}
                        onClick={clearChat}
                        size="small"
                      >
                        Vider la discussion
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Zone de chat */}
              <Grid item xs={12} md={8}>
                <Card sx={{ height: 600 }}>
                  <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        Discussion
                      </Typography>
                      {totalTokensUsed > 0 && (
                        <Typography variant="caption" sx={{ 
                          bgcolor: 'primary.50', 
                          px: 1, 
                          py: 0.5, 
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontWeight: 'bold'
                        }}>
                          Total: 🪙 {totalTokensUsed}
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Messages */}
                    <Box sx={{ 
                      flexGrow: 1, 
                      overflow: 'auto', 
                      border: '1px solid', 
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                      bgcolor: 'grey.50'
                    }}>
                      {chatMessages.length === 0 ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                          Commencez une discussion avec le LLM pour obtenir de l'aide sur la création de prompts et d'exercices.
                        </Typography>
                      ) : (
                        chatMessages.map((message, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                              mb: 2
                            }}
                          >
                            <Paper
                              sx={{
                                p: 2,
                                maxWidth: '80%',
                                bgcolor: message.role === 'user' ? 'var(--color-primary)' : 'white',
                                color: message.role === 'user' ? 'white' : 'var(--color-text)',
                                borderRadius: message.role === 'user' ? '20px 20px 5px 20px' : '20px 20px 20px 5px'
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {message.content}
                                {message.isStreaming && (
                                  <span style={{ 
                                    animation: 'blink 1s infinite',
                                    marginLeft: '2px',
                                    color: message.role === 'user' ? 'white' : 'var(--color-text)'
                                  }}>
                                    ▋
                                  </span>
                                )}
                              </Typography>
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 1, 
                                opacity: 0.7
                              }}>
                                <Typography variant="caption">
                                  {new Date(message.timestamp).toLocaleTimeString()}
                                </Typography>
                                                                 {message.metadata?.tokensUsed && (
                                   <Typography variant="caption" sx={{ 
                                     ml: 1,
                                     fontSize: '0.65rem',
                                     bgcolor: message.role === 'user' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                                     px: 0.5,
                                     py: 0.25,
                                     borderRadius: 0.5,
                                     fontFamily: 'monospace'
                                   }}>
                                     {formatTokensDisplay(message.metadata)}
                                   </Typography>
                                 )}
                              </Box>
                            </Paper>
                          </Box>
                        ))
                      )}
                      
                      {chatLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                          <Paper sx={{ p: 2, borderRadius: '20px 20px 20px 5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={16} />
                              <Typography variant="body2" color="text.secondary">
                                Le LLM réfléchit...
                              </Typography>
                            </Box>
                          </Paper>
                        </Box>
                      )}
                    </Box>
                    
                    {/* Zone de saisie */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        placeholder="Tapez votre message ici..."
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        disabled={chatLoading}
                      />
                      <Button
                        variant="contained"
                        onClick={sendMessage}
                        disabled={chatLoading || !currentMessage.trim()}
                        sx={{ minWidth: 50 }}
                      >
                        <Send />
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Suggestions et raccourcis */}
              <Grid item xs={12} md={4}>
                <Card sx={{ height: 600 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Suggestions et aide
                    </Typography>
                    
                    {/* Suggestions rapides */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Questions fréquentes :
                      </Typography>
                      {[
                        "Comment créer un bon prompt pour les exercices de conjugaison ?",
                        "Quelles variables puis-je utiliser dans mes prompts ?",
                        "Comment améliorer la qualité des exercices générés ?",
                        "Peux-tu m'aider à créer un prompt pour des exercices de lecture ?"
                      ].map((suggestion, index) => (
                        <Button
                          key={index}
                          variant="outlined"
                          size="small"
                          onClick={() => setCurrentMessage(suggestion)}
                          sx={{ 
                            mb: 1, 
                            display: 'block', 
                            textAlign: 'left',
                            textTransform: 'none',
                            justifyContent: 'flex-start'
                          }}
                          fullWidth
                        >
                          {suggestion}
                        </Button>
                      ))}
                    </Box>

                    {/* Suggestions de prompts générées */}
                    {suggestedPrompts.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Suggestions de prompts générées :
                        </Typography>
                        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                          {suggestedPrompts.map((suggestion, index) => (
                            <Accordion key={index} sx={{ mb: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="subtitle2">
                                  {suggestion.title}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  {suggestion.explanation}
                                </Typography>
                                <Paper sx={{ p: 1, bgcolor: 'grey.100' }}>
                                  <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {suggestion.prompt}
                                  </Typography>
                                </Paper>
                                <Button
                                  size="small"
                                  onClick={() => copyPromptToEditor(suggestion.prompt)}
                                  sx={{ mt: 1 }}
                                >
                                  Copier dans l'éditeur
                                </Button>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Informations sur le contexte actuel */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Contexte actuel :
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type de jeu :</strong> {getGameConfig(parametersForm.gameType)?.label || 'Non défini'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Niveau :</strong> {parametersForm.schoolLevel}
                      </Typography>
                      {parametersForm.theme && (
                        <Typography variant="body2">
                          <strong>Thème :</strong> {parametersForm.theme}
                        </Typography>
                      )}
                      {parametersForm.competence && (
                        <Typography variant="body2">
                          <strong>Compétence :</strong> {parametersForm.competence}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Tab Comparaison */}
        {activeTab === 3 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Comparaison de prompts
            </Typography>
            <Alert severity="info">
              Fonctionnalité de comparaison A/B à implémenter
            </Alert>
          </Paper>
        )}

        {/* Tab Optimisation */}
        {activeTab === 4 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Science />
              <Typography variant="h6">
                Optimisation de Prompts
              </Typography>
            </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Comment ça marche :</strong> Ce système évalue automatiquement vos exercices générés sur 3 axes, 
                les adapte si nécessaire, et optimise votre prompt pour de meilleurs résultats.
              </Typography>
            </Alert>

            {/* Instructions d'utilisation */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📋 Comment utiliser l'optimisation
                </Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  <li>
                    <Typography variant="body2" gutterBottom>
                      <strong>1. Générez un exercice</strong> : Utilisez l'onglet "Test de prompts" pour créer un exercice
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" gutterBottom>
                      <strong>2. Lancez l'évaluation</strong> : Cliquez sur "Évaluer et optimiser" dans les résultats
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" gutterBottom>
                      <strong>3. Analysez les résultats</strong> : Consultez les scores et commentaires détaillés
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" gutterBottom>
                      <strong>4. Utilisez le prompt optimisé</strong> : Le système génère automatiquement un prompt amélioré
                    </Typography>
                  </li>
                </Box>
              </CardContent>
            </Card>

            {/* Critères d'évaluation */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🎯 Critères d'Évaluation
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Compétence (1-10)
                      </Typography>
                      <Typography variant="body2">
                        L'exercice cible-t-il spécifiquement la compétence sélectionnée ?
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Erreurs (Oui/Non)
                      </Typography>
                      <Typography variant="body2">
                        Détection d'erreurs de contenu, logiques ou d'incohérences
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Cohérence (1-10)
                      </Typography>
                      <Typography variant="body2">
                        Structure, clarté et respect du format JSON
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Résultats d'optimisation en temps réel */}
            {optimizationData.originalExercise && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📊 Résultats d'Optimisation
                  </Typography>
                  
                  {/* Indicateur de progression */}
                  {optimizationData.isOptimizing && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Progression de l'optimisation :
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {[1, 2, 3, 4].map((step) => (
                          <Box
                            key={step}
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: optimizationData.optimizationStep >= step ? 'primary.main' : 'grey.300',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              color: 'white'
                            }}
                          >
                            {step}
                          </Box>
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {optimizationData.optimizationStep === 1 && 'Évaluation initiale...'}
                        {optimizationData.optimizationStep === 2 && 'Optimisation du prompt...'}
                        {optimizationData.optimizationStep === 3 && 'Régénération finale...'}
                        {optimizationData.optimizationStep === 4 && 'Terminé !'}
                      </Typography>
                    </Box>
                  )}

                  {/* Première évaluation */}
                  {optimizationData.firstEvaluation && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        📊 Première Évaluation
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Box sx={{ 
                            textAlign: 'center', 
                            p: 2, 
                            borderRadius: 1,
                            bgcolor: optimizationData.firstEvaluation.competenceScore < 5 ? 'error.light' : 
                                     optimizationData.firstEvaluation.competenceScore <= 8 ? 'warning.light' : 'success.light'
                          }}>
                            <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                              {optimizationData.firstEvaluation.competenceScore}/10
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Compétence</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box sx={{ 
                            textAlign: 'center', 
                            p: 2, 
                            borderRadius: 1,
                            bgcolor: optimizationData.firstEvaluation.hasErrors ? 'error.light' : 'success.light'
                          }}>
                            <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                              {optimizationData.firstEvaluation.hasErrors ? '❌' : '✅'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Erreurs</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box sx={{ 
                            textAlign: 'center', 
                            p: 2, 
                            borderRadius: 1,
                            bgcolor: (optimizationData.firstEvaluation.consistencyScore || 0) < 5 ? 'error.light' : 
                                     (optimizationData.firstEvaluation.consistencyScore || 0) <= 8 ? 'warning.light' : 'success.light'
                          }}>
                            <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                              {optimizationData.firstEvaluation.consistencyScore || 'N/A'}/10
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Cohérence</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      {optimizationData.firstEvaluation.feedback && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            💬 Commentaires détaillés :
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              {optimizationData.firstEvaluation.feedback}
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  )}


                  {/* Prompt optimisé */}
                  {optimizationData.optimizedPrompt && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        🚀 Prompt Optimisé
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={optimizationData.optimizedPrompt}
                        InputProps={{
                          readOnly: true,
                        }}
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<ContentCopy />}
                          onClick={() => navigator.clipboard.writeText(optimizationData.optimizedPrompt)}
                        >
                          Copier
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            setParametersForm(prev => ({
                              ...prev,
                              customPrompt: optimizationData.optimizedPrompt,
                              useCustomPrompt: true
                            }));
                          }}
                        >
                          Utiliser ce prompt
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Exercice final généré avec le prompt optimisé */}
                  {optimizationData.finalExercise && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        ✨ Exercice Final Généré
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 600, overflow: 'auto' }}>
                        <TestResultPreview
                          gameType={parametersForm.gameType}
                          content={optimizationData.finalExercise}
                        />
                      </Paper>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<ContentCopy />}
                          onClick={() => navigator.clipboard.writeText(JSON.stringify(optimizationData.finalExercise, null, 2))}
                        >
                          Copier le JSON
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            setTestResults({
                              content: optimizationData.finalExercise,
                              loading: false,
                              error: null,
                              metadata: { source: 'optimized' }
                            });
                          }}
                        >
                          Utiliser cet exercice
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Message si aucune optimisation */}
            {!optimizationData.originalExercise && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📊 Historique des Optimisations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Les optimisations récentes apparaîtront ici une fois que vous aurez utilisé le système.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Paper>
        )}

        {renderPromptDialog()}
        {renderBulkGenerateDialog()}
        {renderOptimizationDialog()}
           {/* ✨ NOUVEAU DIALOGUE POUR SAUVEGARDER UN PROMPT TESTÉ */}
        <Dialog 
          open={showPromptEditor} 
          onClose={() => setShowPromptEditor(false)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            Sauvegarder ce prompt
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nom du prompt"
                    value={newPrompt.name}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="ex: Speed Game - Niveau CE2"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Version/Description"
                    value={newPrompt.description}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="ex: Version optimisée pour les verbes"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    label="Contenu du prompt"
                    value={newPrompt.promptContent || currentPromptVersion}
                    onChange={(e) => setNewPrompt(prev => ({ ...prev, promptContent: e.target.value }))}
                    helperText="Ce prompt sera sauvegardé et pourra être réutilisé"
                  />
                </Grid>
                
                {/* Aperçu des paramètres utilisés */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Paramètres utilisés pour ce prompt :
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label={`Type: ${parametersForm.gameType}`} size="small" />
                      <Chip label={`Niveau: ${parametersForm.schoolLevel}`} size="small" />
                      <Chip label={`Difficulté: ${parametersForm.difficulty}`} size="small" />
                      {parametersForm.theme && (
                        <Chip label={`Thème: ${parametersForm.theme}`} size="small" />
                      )}
                      {parametersForm.competence && (
                        <Chip label={`Compétence: ${parametersForm.competence}`} size="small" />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPromptEditor(false)}>
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  await savePromptVersion(
                    newPrompt.promptContent || currentPromptVersion,
                    newPrompt.name,
                    newPrompt.description
                  );
                  setShowPromptEditor(false);
                  setNewPrompt({ name: '', description: '', promptContent: '' });
                  // Afficher un message de succès
                  setErrorMessage('');
                } catch (error) {
                  setErrorMessage('Erreur lors de la sauvegarde du prompt');
                }
              }}
              disabled={!newPrompt.name || (!newPrompt.promptContent && !currentPromptVersion)}
            >
              Sauvegarder
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default PromptEngineeringScreen;